import { useIntl } from '@umijs/max';
import { Empty, Table, Tag } from 'antd';
import { createStyles } from 'antd-style';
import dayjs from 'dayjs';
import React from 'react';
import CopyText from '@/components/CopyText';
import {
  type AdminPaymentOrder,
  adminListOrders,
  type OrderStatus,
} from '@/services/payment';

const STATUS_COLOR: Record<OrderStatus, string> = {
  paid: 'green',
  refunded: 'orange',
  pending: 'blue',
  failed: 'red',
  expired: 'default',
};

const useStyles = createStyles(({ token }) => ({
  compactTable: {
    '& .ant-table-cell': { padding: '8px 10px !important' },
    '& .ant-table-thead > tr > th': {
      padding: '8px 10px !important',
      fontSize: 12,
    },
  },
  orderNo: {
    fontFamily: token.fontFamilyCode,
    fontSize: 11,
  },
}));

export interface OrdersPanelProps {
  /** 传 hex 锁定单用户;不传 = 全局 */
  scopedUserId?: string;
  scrollY?: number;
  /** 值变化时重新拉取(手动上分后刷新) */
  refreshKey?: number;
}

const OrdersPanel: React.FC<OrdersPanelProps> = ({
  scopedUserId,
  scrollY,
  refreshKey,
}) => {
  const { styles } = useStyles();
  const intl = useIntl();
  const t = (id: string, fallback: string) =>
    intl.formatMessage({ id, defaultMessage: fallback });

  const [orders, setOrders] = React.useState<AdminPaymentOrder[]>([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(20);

  const fetch = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminListOrders({
        userId: scopedUserId,
        page,
        pageSize,
      });
      if (res?.success && res.data) {
        setOrders(res.data.orders || []);
        setTotal(res.data.total || 0);
      }
    } finally {
      setLoading(false);
    }
  }, [scopedUserId, page, pageSize, refreshKey]);

  React.useEffect(() => {
    void fetch();
  }, [fetch]);

  const statusLabel = (s: OrderStatus) =>
    intl.formatMessage({ id: `ledger.topup.status.${s}`, defaultMessage: s });

  const columns = [
    {
      title: t('ledger.topup.orderNo', '订单号'),
      dataIndex: 'orderNo',
      key: 'orderNo',
      width: 220,
      render: (v: string) => (
        <CopyText
          text={v}
          tooltips={[v, t('component.copyText.copied', '已复制')]}
          className={styles.orderNo}
        >
          {v.length > 24 ? `${v.slice(0, 12)}…${v.slice(-8)}` : v}
        </CopyText>
      ),
    },
    {
      title: t('ledger.topup.amount', '金额'),
      dataIndex: 'amountCents',
      key: 'amount',
      width: 110,
      align: 'right' as const,
      render: (v: number, r: AdminPaymentOrder) =>
        r.source === 'manual'
          ? t('ledger.topup.manual', '手动')
          : `${(v / 100).toFixed(2)} ${r.currency?.toUpperCase() || 'USD'}`,
    },
    {
      title: t('ledger.topup.credits', '到账 Credits'),
      dataIndex: 'credits',
      key: 'credits',
      width: 120,
      align: 'right' as const,
      render: (v: number) => v.toLocaleString(),
    },
    {
      title: t('ledger.topup.status', '状态'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (s: OrderStatus) => (
        <Tag color={STATUS_COLOR[s]}>{statusLabel(s)}</Tag>
      ),
    },
    {
      title: t('ledger.topup.paidAt', '支付时间'),
      dataIndex: 'paidAt',
      key: 'paidAt',
      width: 170,
      render: (v: string, r: AdminPaymentOrder) =>
        v
          ? dayjs(v).format('YYYY-MM-DD HH:mm:ss')
          : dayjs(r.createdAt).format('YYYY-MM-DD HH:mm'),
    },
  ];

  return (
    <Table<AdminPaymentOrder>
      size="small"
      className={styles.compactTable}
      rowKey="orderNo"
      loading={loading}
      dataSource={orders}
      columns={columns}
      scroll={scrollY ? { y: scrollY } : undefined}
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('ledger.topup.empty', '暂无充值记录')}
          />
        ),
      }}
      pagination={{
        current: page,
        pageSize,
        total,
        showSizeChanger: true,
        pageSizeOptions: [10, 20, 50, 100],
        onChange: (p, ps) => {
          setPage(p);
          setPageSize(ps);
        },
        showTotal: (n) =>
          intl.formatMessage(
            { id: 'ledger.total', defaultMessage: '共 {n} 条' },
            { n },
          ),
      }}
    />
  );
};

export default OrdersPanel;
